import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link as RouterLink } from 'react-router-dom';
import { Container, Typography, Box, Paper, Zoom, Grow, Fade, useMediaQuery, useTheme } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailForm from './EmailForm';
import PrivacyPolicy from './PrivacyPolicy';
import TermsOfService from './TermsOfService';
import { RouteSeo } from './components/RouteSeo';
import './App.css';

const HOME_SEO_TITLE = 'Aylet Aib | Meeting Scheduler & WhatsApp Analytics Platform';
const HOME_SEO_DESCRIPTION =
  'Aylet Aib — meeting scheduling and analytics powered by WhatsApp. Register for updates and streamline how you connect with clients and teams.';

const App: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isExtraSmall = useMediaQuery('(max-width:480px)');
  const isMediumLarge = useMediaQuery('(min-width:769px) and (max-width:899px)');

  // Add viewport meta tag for mobile responsiveness
  useEffect(() => {
    // Check if viewport meta tag exists
    let viewport = document.querySelector('meta[name="viewport"]');
    if (!viewport) {
      // If it doesn't exist, create it
      viewport = document.createElement('meta');
      viewport.setAttribute('name', 'viewport');
      document.head.appendChild(viewport);
    }
    // Set the content attribute
    viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');
    
    return () => {
      // Cleanup (although usually not needed for meta tags)
    };
  }, []);

  // Get the consistent container style for mobile components
  const getMobileContainerStyle = () => {
    if (!isMobile && !isMediumLarge) return {};
    
    return {
      width: '100%',
      maxWidth: '100%',
      mx: 'auto',
      borderRadius: { xs: '12px', sm: '14px', md: '16px' },
      border: '1px solid rgba(231, 231, 231, 0.9)',
      boxShadow: isExtraSmall 
        ? '0 6px 15px rgba(0, 0, 0, 0.1)' 
        : isMediumLarge
          ? '0 8px 25px rgba(0, 0, 0, 0.07)'
          : '0 8px 20px rgba(0, 0, 0, 0.08)',
    };
  };

  return (
    <Router future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
      <Routes>
        <Route path="/" element={
          <>
          <RouteSeo title={HOME_SEO_TITLE} canonicalPath="/" description={HOME_SEO_DESCRIPTION} />
          <Box component="main" className="app-background">
            <Container maxWidth="lg" className="app-container">
              {/* Header */}
              <Fade in={true} timeout={1000}>
                <Box className="header" sx={{ mb: isMobile ? 3 : isTablet ? 4 : 6 }}>
                  <Typography component="h1" variant="h2" className="header-title" sx={{ 
                    fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' }, 
                    fontWeight: 'bold', 
                    mb: isMobile ? 1 : 2 
                  }}>
                    Aylet Aib
                  </Typography>
                  <Typography variant="h6" component="p" className="header-subtitle" sx={{ 
                    fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' }, 
                    color: '#546e7a', 
                    maxWidth: '600px', 
                    mx: 'auto', 
                    lineHeight: 1.5,
                    px: isMobile ? 1 : 0
                  }}>
                    Your innovative meeting scheduler & analytics platform powered by modern technology.{' '}
                    <RouterLink to="/#register">Register for updates</RouterLink>.
                  </Typography>
                </Box>
              </Fade>

              {/* Features */}
              <Box id="features" className="features" sx={{ 
                flexDirection: { xs: 'column', md: 'row' },
                gap: { xs: 2.5, sm: 3, md: 3 },
                mb: { xs: 3, sm: 3.5, md: 4 },
                width: '100%',
                mx: 'auto'
              }}>
                <Zoom in={true} style={{ transitionDelay: '300ms' }}>
                  <Paper className="feature-card" elevation={0} sx={{
                    p: { xs: 2, sm: 2.5, md: 3 },
                    mb: { xs: 0, md: 0 },
                    borderRadius: { xs: '12px', sm: '14px', md: '16px' },
                    border: (isMobile || isMediumLarge) ? '1px solid rgba(231, 231, 231, 0.9)' : '1px solid rgba(231, 231, 231, 0.8)',
                    boxShadow: (isMobile || isMediumLarge)
                      ? (isExtraSmall 
                         ? '0 6px 15px rgba(0, 0, 0, 0.1)' 
                         : isMediumLarge
                           ? '0 8px 25px rgba(0, 0, 0, 0.07)'
                           : '0 8px 20px rgba(0, 0, 0, 0.08)')
                      : '0 10px 30px rgba(0, 0, 0, 0.05)',
                    background: (isMobile || isMediumLarge) ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.9)',
                    transform: 'translateZ(0)',
                    backfaceVisibility: 'hidden',
                    WebkitFontSmoothing: 'subpixel-antialiased',
                    width: '100%',
                    ...getMobileContainerStyle()
                  }}>
                    <Typography component="h2" variant="h5" className="feature-title" sx={{
                      fontSize: { xs: '1.25rem', sm: '1.4rem', md: '1.5rem' },
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <CalendarMonthIcon sx={{ mr: 1, color: '#1976d2', fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.5rem' } }} />
                      Schedule Meetings
                    </Typography>
                    <Typography variant="body1" className="feature-description" sx={{
                      fontSize: { xs: '0.9rem', sm: '1rem', md: '1rem' }
                    }}>
                      Streamline your day with our intuitive meeting scheduling system. Connect with clients and team members effortlessly.
                    </Typography>
                  </Paper>
                </Zoom>
                
                <Zoom in={true} style={{ transitionDelay: '600ms' }}>
                  <Paper className="feature-card" elevation={0} sx={{
                    p: { xs: 2, sm: 2.5, md: 3 },
                    borderRadius: { xs: '12px', sm: '14px', md: '16px' },
                    border: (isMobile || isMediumLarge) ? '1px solid rgba(231, 231, 231, 0.9)' : '1px solid rgba(231, 231, 231, 0.8)',
                    boxShadow: (isMobile || isMediumLarge)
                      ? (isExtraSmall 
                         ? '0 6px 15px rgba(0, 0, 0, 0.1)' 
                         : isMediumLarge
                           ? '0 8px 25px rgba(0, 0, 0, 0.07)'
                           : '0 8px 20px rgba(0, 0, 0, 0.08)')
                      : '0 10px 30px rgba(0, 0, 0, 0.05)',
                    background: (isMobile || isMediumLarge) ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.9)',
                    transform: 'translateZ(0)',
                    backfaceVisibility: 'hidden',
                    WebkitFontSmoothing: 'subpixel-antialiased',
                    width: '100%',
                    ...getMobileContainerStyle()
                  }}>
                    <Typography component="h2" variant="h5" className="feature-title" sx={{
                      fontSize: { xs: '1.25rem', sm: '1.4rem', md: '1.5rem' },
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <PhotoCameraIcon sx={{ mr: 1, color: '#1976d2', fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.5rem' } }} />
                      Image Analytics
                    </Typography>
                    <Typography variant="body1" className="feature-description" sx={{
                      fontSize: { xs: '0.9rem', sm: '1rem', md: '1rem' }
                    }}>
                      Leverage our powerful image analysis technology to extract insights and data from your visual content automatically.
                    </Typography>
                  </Paper>
                </Zoom>
              </Box>

              {/* Email Registration */}
              <Grow in={true} timeout={1000}>
                <Box id="register" className="email-registration" sx={{position: "relative", zIndex: 1, 
                  p: { xs: 2, sm: 2.5, md: 3 },
                  pb: { xs: 4.5, sm: 5, md: 5.5 },
                  mb: { xs: 2.5, md: 4 },
                  ...getMobileContainerStyle()
                }}>
                  <Typography component="h2" variant="h4" className="email-title" sx={{
                    fontSize: { xs: '1.4rem', sm: '1.6rem', md: '2rem' },
                    mb: { xs: 0.5, md: 1 }
                  }}>
                    Join Our Community
                  </Typography>
                  <Typography variant="body1" className="email-subtitle" sx={{
                    fontSize: { xs: '0.9rem', sm: '1rem', md: '1rem' },
                    mb: { xs: 1, md: 1.5 }
                  }}>
                    Register your email to receive updates, tips, and early access to new features.
                  </Typography>
                  <EmailForm />
                </Box>
              </Grow>

              {/* WhatsApp Business Policy Compliance Notice */}
              <Fade in={true} timeout={1500}>
                <Box className="whatsapp-policy" sx={{
                  p: { xs: 1.5, sm: 2, md: 2.5 },
                  mx: { xs: 0, md: 2 },
                  borderRadius: { xs: '12px', md: '16px' },
                  ...(isMobile || isMediumLarge ? { width: '100%' } : {})
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}>
                    <WhatsAppIcon sx={{ color: '#128C7E', mr: 1, fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' } }} />
                    <Typography variant="body1" align="center" sx={{ fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' } }}>
                      Our business complies with the WhatsApp Business Policy
                    </Typography>
                  </Box>
                  <Typography variant="body2" align="center" sx={{ mt: 1, fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.875rem' } }}>
                    <a 
                      href="https://business.whatsapp.com/policy" 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      Learn more about WhatsApp Business Policy
                    </a>
                  </Typography>
                </Box>
              </Fade>

              {/* Footer */}
              <Box component="nav" aria-label="Footer and legal" className="footer" sx={{ 
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 1, sm: 0 },
                mt: { xs: 2, sm: 3, md: 4 },
                pt: { xs: 1.5, sm: 2 }
              }}>
                <Typography variant="body2" className="footer-text" sx={{ fontSize: { xs: '0.8rem', md: '0.875rem' } }}>
                  &copy; {new Date().getFullYear()} Aylet Aib. All rights reserved
                </Typography>
                <Typography variant="caption" component="div" className="footer-links" sx={{ fontSize: { xs: '0.75rem', md: '0.8rem' } }}>
                  <RouterLink to="/#features">Features</RouterLink>
                  {' · '}
                  <RouterLink to="/#register">Register</RouterLink>
                  {' · '}
                  <RouterLink to="/privacy">
                    Privacy Policy
                  </RouterLink>
                  {' · '}
                  <RouterLink to="/terms">
                    Terms of Service
                  </RouterLink>
                </Typography>
              </Box>
            </Container>
          </Box>
          </>
        } />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
      </Routes>
    </Router>
  );
};

export default App;
